// components/TenantLanding/Login/RegisterForms/RegisterStage1.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import "./RegisterStage1.css";
import axios from "axios";
import { API_BASE } from "../../../ApiUrl";

import Account1 from "./Account1";
import Identity1 from "./Identity1";
import Location1 from "./Location1";
import Professional1 from "./Professional1";
import Consent1 from "./Consent1";

const API_BASE_URL = API_BASE;

const getCSRFToken = () => {
  const name = "csrftoken";
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [key, value] = cookie.trim().split('=');
    if (key === name) return value;
  }
  return "";
};

const emptyFormData = {
  tenant: 1,
  tenant_id: 1,
  email: "",
  mobile_country_code: "+91",
  mobile_number: "",
  password: "",
  confirm_password: "",
  mfa_method: "Authenticator",

  first_name: "",
  middle_name: "",
  last_name: "",
  preferred_name: "",
  name_display_order: "Given-Family",

  country_of_residence: "",
  city: "",
  time_zone: "Asia/Kolkata",

  primary_industry: "",
  primary_scope: "",
  self_declared_career_stage: "",
  current_job_title: "",
  initial_experience_band: "",
  highest_qualification_level: "",

  existing_resume: null,
  profile_photo: null,

  terms: false,
  privacy: false,
  resume_processing: false,
  marketing: false,

  referral_source: "",
  referral_code: "",
};

const RegisterStage1 = () => {
  const navigate = useNavigate();
  const { userId: userIdParam } = useParams(); // supports /register-stage1/:userId
  const [searchParams] = useSearchParams();    // supports /register-stage1?userId=9

  // Resolve edit mode + target user id from either route param or query string
  const editUserId = userIdParam || searchParams.get("userId") || searchParams.get("edit");
  const isEditMode = Boolean(editUserId);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);

  const [originalEmail, setOriginalEmail] = useState(""); // NEW

  // In edit mode we skip OTP gating entirely
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(isEditMode); // treat as verified in edit mode
  const [otpValue, setOtpValue] = useState("");
  const [otpType, setOtpType] = useState("EMAIL");
  const [emailChecked, setEmailChecked] = useState(isEditMode);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [scopeOptions, setScopeOptions] = useState([]);
  const [hasExistingResumeOnFile, setHasExistingResumeOnFile] = useState(false);
  const [hasExistingPhotoOnFile, setHasExistingPhotoOnFile] = useState(false);

  // Keep the raw fetched user + application id, needed for resubmit payload shape
  const [sourceUser, setSourceUser] = useState(null);
  const [applicationId, setApplicationId] = useState(null);

  const [formData, setFormData] = useState(emptyFormData);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resumeFileName, setResumeFileName] = useState("");
  const [photoFileName, setPhotoFileName] = useState("");
  const [applicationStatus, setApplicationStatus] = useState("");

  useEffect(() => {
    fetchIndustries();
  }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchUserForEdit(editUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editUserId]);

const fetchIndustries = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/reference-values/`);
      const industries = res.data.data
        .filter(
          (item) =>
            item.is_active === true &&
            (item.option_set === "INDUSTRY" ||
              item.option_set?.option_type === "INDUSTRY" ||
              item.option_set_value?.option_type === "INDUSTRY")
        )
        .sort((a, b) => a.sort_order - b.sort_order);
      setIndustryOptions(industries);
    } catch (error) {
      console.error("Error fetching industries:", error);
    }
  };

  useEffect(() => {
    if (formData.primary_industry) {
      fetchScopes(formData.primary_industry);
    } else {
      setScopeOptions([]);
    }
  }, [formData.primary_industry]);

  const fetchScopes = async (industryId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/scope-catalogs/`);
      const scopes = res.data.data
        .filter(
          (item) =>
            item.industry === Number(industryId) &&
            item.is_active === true &&
            item.status === "PUBLISHED"
        )
        .sort((a, b) => a.id - b.id);
      setScopeOptions(scopes);
    } catch (error) {
      console.error("Error fetching scopes:", error);
    }
  };

  // ============================================
  // Fetch existing user (EDIT MODE) and hydrate form
  // ============================================
  const fetchUserForEdit = async (id) => {
    setInitialLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_BASE_URL}/users/${id}/`);
      const userData = res?.data?.data;

      if (!userData) {
        throw new Error("No user data returned from server");
      }

      setSourceUser(userData);

      const app = userData.registration_applications?.[0];
      if (app?.id) setApplicationId(app.id);
      setApplicationStatus(app?.status || "");
      const snapshot = app?.stage1_snapshot || {};
      const profile = userData.professional_profile || {};
      const emailOnFile = userData.email || snapshot.email || "";
      setOriginalEmail(emailOnFile);




      // Consent records give us the granted flags reliably
      const consentMap = {};
      (userData.consent_records || []).forEach((c) => {
        if (c && c.consent_type) {
          consentMap[c.consent_type] = c.is_granted;
        }
      });

      setFormData((prev) => ({
        ...prev,
        tenant: userData.tenant ?? prev.tenant,
        tenant_id: userData.tenant ?? prev.tenant_id,
        email: emailOnFile,
        mobile_country_code: userData.mobile_country_code || snapshot.mobile_country_code || "+91",
        mobile_number: userData.mobile_number || snapshot.mobile_number || "",
        password: "",
        confirm_password: "",
        mfa_method:
          (userData.mfa_method &&
            String(userData.mfa_method).charAt(0) + String(userData.mfa_method).slice(1).toLowerCase()) ||
          snapshot.mfa_method ||
          "Authenticator",

        first_name: profile.first_name || snapshot.first_name || "",
        middle_name: profile.middle_name || snapshot.middle_name || "",
        last_name: profile.last_name || snapshot.last_name || "",
        preferred_name: profile.preferred_name || snapshot.preferred_name || "",
        name_display_order:
          profile.name_display_order || snapshot.name_display_order || "Given-Family",

        country_of_residence:
          profile.country_of_residence || snapshot.country_of_residence || "",
        city: profile.city || snapshot.city || "",
        time_zone: profile.timezone || snapshot.time_zone || "Asia/Kolkata",

        primary_industry: profile.primary_industry
          ? String(profile.primary_industry)
          : snapshot.primary_industry || "",
        primary_scope: profile.primary_scope
          ? String(profile.primary_scope)
          : snapshot.primary_scope || "",
        self_declared_career_stage:
          profile.self_declared_career_stage || snapshot.self_declared_career_stage || "",
        current_job_title: profile.current_job_title || snapshot.current_job_title || "",
        initial_experience_band:
          profile.initial_experience_band || snapshot.initial_experience_band || "",
        highest_qualification_level:
          profile.highest_qualification_level ||
          snapshot.highest_qualification_level ||
          "",

        existing_resume: null,
        profile_photo: null,

        terms: consentMap.TERMS ?? snapshot.terms ?? false,
        privacy: consentMap.PRIVACY ?? snapshot.privacy ?? false,
        resume_processing:
          consentMap.RESUME_PROCESSING ?? snapshot.resume_processing ?? false,
        marketing: consentMap.MARKETING ?? snapshot.marketing ?? false,

        referral_source: userData.referral_source || snapshot.referral_source || "",
        referral_code: userData.referral_code || snapshot.referral_code || "",
      }));

      // Defensive: existing_resume / profile_photo_evidence might be a full URL,
      // a relative path, or an object depending on backend serialization.
      try {
        const resumePath = profile.existing_resume;
        if (resumePath && typeof resumePath === "string") {
          setResumeFileName(resumePath.split("/").pop());
          setHasExistingResumeOnFile(true);
        } else if (resumePath) {
          setHasExistingResumeOnFile(true);
        }
      } catch (fileErr) {
        console.warn("Could not parse resume filename:", fileErr);
      }

      try {
        const photoPath = profile.profile_photo_evidence;
        if (photoPath && typeof photoPath === "string") {
          setPhotoFileName(photoPath.split("/").pop());
          setHasExistingPhotoOnFile(true);
        } else if (photoPath) {
          setHasExistingPhotoOnFile(true);
        }
      } catch (fileErr) {
        console.warn("Could not parse photo filename:", fileErr);
      }

      setEmailChecked(true);
      setOtpVerified(true);
    } catch (err) {
      console.error("Error fetching user for edit:", err);
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load your existing application. Please try again."
      );
    } finally {
      setInitialLoading(false);
    }
  };

  const isEmailLocked = isEditMode && applicationStatus === "RETURNED";

  const emailChanged =
    isEditMode &&
    formData.email.trim().toLowerCase() !== originalEmail.trim().toLowerCase();
const requiresOtp = !isEditMode || (emailChanged && !isEmailLocked);


useEffect(() => {
  if (!isEditMode || isEmailLocked) return;
  if (formData.email.trim().toLowerCase() === originalEmail.trim().toLowerCase()) {
    setOtpVerified(true);
    setEmailChecked(true);
    setOtpSent(false);
    setOtpValue("");
  } else {
    setOtpVerified(false);
    setEmailChecked(false);
    setOtpSent(false);
    setOtpValue("");
  }
}, [formData.email, originalEmail, isEditMode, isEmailLocked]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
      if (name === "existing_resume") {
        setResumeFileName(files[0].name);
      } else if (name === "profile_photo") {
        setPhotoFileName(files[0].name);
      }
    }
  };

  // ============================================
  // API: CHECK USER
  // ============================================
  const checkUser = async (email) => {
    try {
      const response = await fetch(`${API_BASE_URL}/check-user/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRFToken(),
        },
        body: JSON.stringify({
          tenant_id: formData.tenant_id || "string",
          email: email,
        }),
      });

      if (response.status === 200) {
        const data = await response.json();
        return { exists: data.exists || false, data };
      } else if (response.status === 404) {
        return { exists: false };
      } else {
        const text = await response.text();
        throw new Error(`Server error: ${response.status} - ${text}`);
      }
    } catch (error) {
      console.error("Check user error:", error);
      if (error.message.includes("500")) {
        return { exists: false, serverError: true };
      }
      throw error;
    }
  };

  // ============================================
  // API: REGISTER (create)
  // ============================================
  const registerUser = async (payload) => {
    const formDataToSend = new FormData();

    Object.keys(payload).forEach((key) => {
      if (payload[key] !== null && payload[key] !== undefined) {
        if (key === "existing_resume" || key === "profile_photo") {
          if (payload[key] instanceof File) {
            formDataToSend.append(key, payload[key]);
          }
        } else {
          formDataToSend.append(key, String(payload[key]));
        }
      }
    });

    const response = await fetch(`${API_BASE_URL}/register/`, {
      method: "POST",
      headers: {
        "X-CSRFToken": getCSRFToken(),
      },
      body: formDataToSend,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Registration failed: ${response.status}`);
    }

    return await response.json();
  };

  // ============================================
  // API: RESUBMIT (edit)
  // registration-applications/user-id/<int:user_id>/resubmit/
  // ============================================
  const resubmitUser = async (userIdToResubmit, payload) => {
    const formDataToSend = new FormData();

    Object.keys(payload).forEach((key) => {
      if (payload[key] !== null && payload[key] !== undefined) {
        if (key === "existing_resume" || key === "profile_photo") {
          if (payload[key] instanceof File) {
            formDataToSend.append(key, payload[key]);
          }
          // if not a new File, skip - don't overwrite existing file with nothing
        } else if (key === "password") {
          // Only send password if the user actually typed a new one
          if (payload[key]) {
            formDataToSend.append(key, String(payload[key]));
          }
        } else {
          formDataToSend.append(key, String(payload[key]));
        }
      }
    });

    const response = await fetch(
      `${API_BASE_URL}/registration-applications/user-id/${userIdToResubmit}/resubmit/`,
      {
        method: "POST",
        headers: {
          "X-CSRFToken": getCSRFToken(),
        },
        body: formDataToSend,
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Resubmission failed: ${response.status}`);
    }

    return await response.json();
  };

  

  // ============================================
  // API: OTP REQUEST / VERIFY (unchanged, unused in edit mode)
  // ============================================
  const requestOTP = async (type = "EMAIL") => {
    try {
      const payload = {
        otp_type: type,
        tenant_id: formData.tenant_id || "string",
        tenant: formData.tenant || "string",
        email: formData.email,
        password: formData.password,
        new_value: "",
        mobile_country_code: formData.mobile_country_code,
      };

      const response = await fetch(`${API_BASE_URL}/otp-request/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRFToken(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `OTP request failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("OTP Request error:", error);
      throw error;
    }
  };

  const verifyOTP = async () => {
    try {
      const payload = {
        otp_type: otpType,
        tenant_id: formData.tenant_id || "string",
        tenant: formData.tenant || "string",
        email: formData.email,
        new_value: "",
        mobile_country_code: formData.mobile_country_code,
        otp: otpValue,
        new_password: "",
      };

      const response = await fetch(`${API_BASE_URL}/otp-verify/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRFToken(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `OTP verification failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("OTP Verify error:", error);
      throw error;
    }
  };

  const validateStep = (stepNum) => {
    const newErrors = {};

    if (stepNum === 1) {
      if (!formData.email.trim()) {
        newErrors.email = "Email address is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
      if (!formData.mobile_number.trim()) {
        newErrors.mobile_number = "Mobile number is required";
      } else if (!/^\d{10,15}$/.test(formData.mobile_number.replace(/\s/g, ""))) {
        newErrors.mobile_number = "Please enter a valid mobile number (10-15 digits)";
      }

      if (!isEditMode) {
        // Password required only on fresh registration
        if (!formData.password) {
          newErrors.password = "Password is required";
        } else if (formData.password.length < 8) {
          newErrors.password = "Password must be at least 8 characters";
        }
        if (formData.password !== formData.confirm_password) {
          newErrors.confirm_password = "Passwords do not match";
        }
        if (requiresOtp && !otpVerified) {
          newErrors.otp = "Please verify your email with OTP";
        }
      } else if (formData.password || formData.confirm_password) {
        // Edit mode: only validate password if user is changing it
        if (formData.password.length < 8) {
          newErrors.password = "Password must be at least 8 characters";
        }
        if (formData.password !== formData.confirm_password) {
          newErrors.confirm_password = "Passwords do not match";
        }
      }
    }

    if (stepNum === 2) {
      if (!formData.first_name.trim()) {
        newErrors.first_name = "First name is required";
      }
      if (!formData.name_display_order) {
        newErrors.name_display_order = "Name display order is required";
      }
    }

    if (stepNum === 3) {
      if (!formData.country_of_residence) {
        newErrors.country_of_residence = "Country of residence is required";
      }
      if (!formData.city.trim()) {
        newErrors.city = "City is required";
      }
      if (!formData.time_zone) {
        newErrors.time_zone = "Time zone is required";
      }
    }

    if (stepNum === 4) {
      if (!formData.primary_industry) {
        newErrors.primary_industry = "Primary industry is required";
      }
      if (!formData.primary_scope) {
        newErrors.primary_scope = "Primary scope is required";
      }
      if (!formData.self_declared_career_stage) {
        newErrors.self_declared_career_stage = "Career stage is required";
      }
      if (!formData.initial_experience_band) {
        newErrors.initial_experience_band = "Experience band is required";
      }
      if (!formData.highest_qualification_level) {
        newErrors.highest_qualification_level = "Highest qualification is required";
      }
    }

    if (stepNum === 5) {
      if (!formData.terms) {
        newErrors.terms = "You must accept the platform terms";
      }
      if (!formData.privacy) {
        newErrors.privacy = "You must acknowledge the privacy notice";
      }
      const resumeConsentRelevant = formData.existing_resume || hasExistingResumeOnFile;
      if (resumeConsentRelevant && !formData.resume_processing) {
        newErrors.resume_processing = "You must permit resume parsing when a resume is uploaded";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================
  // Handle Email Check (works in both create and edit mode)
  // ============================================
const handleEmailCheck = async () => {
  if (!formData.email.trim()) {
    setErrors((prev) => ({ ...prev, email: "Please enter an email address" }));
    return;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    setErrors((prev) => ({ ...prev, email: "Please enter a valid email address" }));
    return;
  }

  setLoading(true);
  setError("");

  try {
    // In edit mode, if email hasn't changed, we don't need to check
    if (isEditMode && !emailChanged) {
      setEmailChecked(true);
      setOtpVerified(true);
      setLoading(false);
      return;
    }

    // Always check against the current formData.email (the new email)
    const result = await checkUser(formData.email);

    if (result.exists) {
      setError("This email is already registered. Please use a different email.");
      setEmailChecked(false);
      setLoading(false);
      return;
    }

    if (result.serverError) {
      setError("Server error checking email. Continuing...");
    }

    setEmailChecked(true);
    
    // Send OTP to the NEW email
    await handleRequestOTP();
    
    await Swal.fire({
      icon: "success",
      title: "Email Available!",
      text: `A verification code has been sent to ${formData.email}`,
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (err) {
    setError(err.message || "Failed to check email. Please try again.");
  } finally {
    setLoading(false);
  }
};

const handleRequestOTP = async () => {
  setLoading(true);
  setError("");

  try {
    // Make sure we're using the current email from formData
    const payload = {
      otp_type: "EMAIL",
      tenant_id: formData.tenant_id || "string",
      tenant: formData.tenant || "string",
      email: formData.email, // This is the current email (new one if changed)
      new_value: "",
      mobile_country_code: formData.mobile_country_code,
    };

    const response = await fetch(`${API_BASE_URL}/otp-request/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": getCSRFToken(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `OTP request failed: ${response.status}`);
    }

    setOtpSent(true);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("OTP Request error:", error);
    throw error;
  } finally {
    setLoading(false);
  }
};

const handleVerifyOTP = async () => {
  if (!otpValue || otpValue.length < 4) {
    setErrors((prev) => ({ ...prev, otp: "Please enter the 6-digit OTP" }));
    return;
  }

  setLoading(true);
  setError("");

  try {
    const payload = {
      otp_type: "EMAIL",
      tenant_id: formData.tenant_id || "string",
      tenant: formData.tenant || "string",
      email: formData.email, // Use the current email
      new_value: "",
      mobile_country_code: formData.mobile_country_code,
      otp: otpValue,
      new_password: "",
    };

    const response = await fetch(`${API_BASE_URL}/otp-verify/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": getCSRFToken(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `OTP verification failed: ${response.status}`);
    }

    setOtpVerified(true);
    await Swal.fire({
      icon: "success",
      title: "Email Verified!",
      text: `Your new email ${formData.email} has been successfully verified.`,
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (err) {
    setError(err.message || "OTP verification failed. Please try again.");
  } finally {
    setLoading(false);
  }
};



  const handleResendOTP = async () => {
    await handleRequestOTP();
  };

  const handleNext = () => {
    if (step === 1 && requiresOtp) {
      if (!emailChecked) {
        setErrors((prev) => ({ ...prev, email: "Please check your email first" }));
        return;
      }
      if (!otpVerified) {
        setErrors((prev) => ({ ...prev, otp: "Please verify your email with OTP" }));
        return;
      }
    }

    if (!validateStep(step)) {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.focus();
        }
      }
      return;
    }
    setStep((prev) => Math.min(prev + 1, 5));
  };

  const handlePrevious = () => {
    if (step === 1) {
      navigate(-1);
      return;
    }
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateStep(5)) {
      setStep(5);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        tenant: formData.tenant || "",
        email: formData.email,
        mobile_country_code: formData.mobile_country_code,
        mobile_number: formData.mobile_number,
        password: formData.password, // resubmitUser only sends this if non-empty
        mfa_method: formData.mfa_method,
        referral_source: formData.referral_source || "",
        referral_code: formData.referral_code || "",
        first_name: formData.first_name,
        middle_name: formData.middle_name || "",
        last_name: formData.last_name || "",
        preferred_name: formData.preferred_name || "",
        name_display_order: formData.name_display_order,
        country_of_residence: formData.country_of_residence,
        city: formData.city,
        time_zone: formData.time_zone,
        primary_industry: formData.primary_industry,
        primary_scope: formData.primary_scope,
        self_declared_career_stage: formData.self_declared_career_stage,
        current_job_title: formData.current_job_title || "",
        initial_experience_band: formData.initial_experience_band,
        highest_qualification_level: formData.highest_qualification_level,
        existing_resume: formData.existing_resume,
        profile_photo: formData.profile_photo,
        terms: formData.terms,
        privacy: formData.privacy,
        resume_processing: formData.resume_processing,
        marketing: formData.marketing,
      };

      let result;
      if (isEditMode) {
        result = await resubmitUser(editUserId, payload);

        await Swal.fire({
          icon: "success",
          title: "Application Updated!",
          text: "Your changes have been resubmitted for review.",
          timer: 2500,
          showConfirmButton: true,
        });

        navigate(`/profile`); // adjust to your actual detail route
      } else {
        result = await registerUser(payload);

        await Swal.fire({
          icon: "success",
          title: "Account Created!",
          text: "Your account has been created successfully. You can now login.",
          timer: 2500,
          showConfirmButton: true,
        });

        navigate("/Oceanstar/Login");
      }

      console.log("📝 Result:", result);
    } catch (err) {
      const action = isEditMode ? "Resubmission" : "Registration";
      setError(err.message || `${action} failed. Please try again.`);
      Swal.fire({
        icon: "error",
        title: `${action} Failed`,
        text: err.message || `Unable to ${isEditMode ? "update" : "create"} account. Please try again.`,
        showConfirmButton: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const stepClass = (stepNum) => {
    if (stepNum === step) return "register-stage1-step register-stage1-step--active";
    if (stepNum < step) return "register-stage1-step register-stage1-step--done";
    return "register-stage1-step";
  };

  if (initialLoading) {
    return (
      <div className="register-stage1-page">
        <div className="register-stage1-wrapper">
          <p>Loading your application...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="register-stage1-page">
      <div className="register-stage1-top-nav">
        <div className="register-stage1-nav-logo">🔍</div>
        <div className="register-stage1-nav-text">
          <p className="register-stage1-nav-title">QUALION</p>
          <p className="register-stage1-nav-subtitle">Form-Field & Auto-Resume Builder</p>
        </div>
      </div>

      <div className="register-stage1-wrapper">
        <div className="register-stage1-header">
          <button
            type="button"
            className="register-stage1-header__back-btn"
            onClick={handlePrevious}
            aria-label="Back"
          >
            ←
          </button>
          <div className="register-stage1-header__title-wrapper">
            <h2 className="register-stage1-header__title">
              {isEditMode ? "Update Your Application" : "Create Your Account"}
            </h2>
            <p className="register-stage1-header__subtitle">
              {isEditMode
                ? "Review and update the details below, then resubmit for approval."
                : "Stage 1: Provide the minimum information needed to create, secure and route your account. Detailed identity, qualifications and project records will be added in Stage 2."}
            </p>
          </div>
        </div>

        {error && (
          <div className="register-stage1-alert register-stage1-alert--error" role="alert">
            <span className="register-stage1-alert__message">
              <strong>Error:</strong> {error}
            </span>
            <button
              type="button"
              className="register-stage1-alert__close-btn"
              onClick={() => setError("")}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        )}

        <div className="register-stage1-stepper">
          <div className={stepClass(1)} onClick={() => { if (validateStep(1)) setStep(1); }}>
            <span className="register-stage1-step__number">1</span>
            <div className="register-stage1-step__text">
              <span className="register-stage1-step__label">Account</span>
            </div>
          </div>
          <div className="register-stage1-step__line" />
          <div className={stepClass(2)} onClick={() => { if (validateStep(1)) setStep(2); }}>
            <span className="register-stage1-step__number">2</span>
            <div className="register-stage1-step__text">
              <span className="register-stage1-step__label">Identity</span>
            </div>
          </div>
          <div className="register-stage1-step__line" />
          <div className={stepClass(3)} onClick={() => { if (validateStep(1) && validateStep(2)) setStep(3); }}>
            <span className="register-stage1-step__number">3</span>
            <div className="register-stage1-step__text">
              <span className="register-stage1-step__label">Location</span>
            </div>
          </div>
          <div className="register-stage1-step__line" />
          <div className={stepClass(4)} onClick={() => { if (validateStep(1) && validateStep(2) && validateStep(3)) setStep(4); }}>
            <span className="register-stage1-step__number">4</span>
            <div className="register-stage1-step__text">
              <span className="register-stage1-step__label">Professional</span>
            </div>
          </div>
          <div className="register-stage1-step__line" />
          <div className={stepClass(5)} onClick={() => { if (validateStep(1) && validateStep(2) && validateStep(3) && validateStep(4)) setStep(5); }}>
            <span className="register-stage1-step__number">5</span>
            <div className="register-stage1-step__text">
              <span className="register-stage1-step__label">Consent</span>
            </div>
          </div>
        </div>

        <form className="register-stage1-form" onSubmit={handleSubmit}>
          {step === 1 && (
            <Account1
  formData={formData}
  errors={errors}
  loading={loading}
  handleChange={handleChange}
  otpSent={otpSent}
  otpVerified={otpVerified}
  otpValue={otpValue}
  setOtpValue={setOtpValue}
  handleEmailCheck={handleEmailCheck}
  handleVerifyOTP={handleVerifyOTP}
  handleResendOTP={handleResendOTP}
  showPassword={showPassword}
  setShowPassword={setShowPassword}
  showConfirmPassword={showConfirmPassword}
  setShowConfirmPassword={setShowConfirmPassword}
  handleNext={handleNext}
  handlePrevious={handlePrevious}
  isEditMode={isEditMode}
  emailChanged={emailChanged}
  isEmailLocked={isEmailLocked}
  originalEmail={originalEmail}
/>
          )}

          {step === 2 && (
            <Identity1
              formData={formData}
              errors={errors}
              loading={loading}
              handleChange={handleChange}
              handleNext={handleNext}
              handlePrevious={handlePrevious}
            />
          )}

          {step === 3 && (
            <Location1
              formData={formData}
              errors={errors}
              loading={loading}
              handleChange={handleChange}
              handleNext={handleNext}
              handlePrevious={handlePrevious}
            />
          )}

          {step === 4 && (
            <Professional1
              formData={formData}
              errors={errors}
              loading={loading}
              handleChange={handleChange}
              handleNext={handleNext}
              handlePrevious={handlePrevious}
              industryOptions={industryOptions}
              scopeOptions={scopeOptions}
            />
          )}

          {step === 5 && (
            <Consent1
              formData={formData}
              errors={errors}
              loading={loading}
              handleChange={handleChange}
              handleFileChange={handleFileChange}
              resumeFileName={resumeFileName}
              photoFileName={photoFileName}
              handlePrevious={handlePrevious}
              isEditMode={isEditMode}
              hasExistingResumeOnFile={hasExistingResumeOnFile}
              hasExistingPhotoOnFile={hasExistingPhotoOnFile}
            />
          )}
        </form>
      </div>
    </div>
  );
};

export default RegisterStage1;